import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { ResourceInfoDc, ResourceType } from "@evergis/api";

import { dialogResourceSelector } from "../../../../../../ducks/dialogs/selectors";
import { createFilterCopyTask } from "../../../../../../evergis/core/tools/createFilterCopyTask";
import { useTablePreview } from "../../../../../../hooks/preview/usePreview";
import { useTable } from "../../../../../../hooks/table/useTable";
import { TaskDataType } from "../../../../../../interfaces/scheduler";
import { getResourceName } from "../../../../../../utils/resourceName";
import { getMetaFromResource, useResourceMeta } from "../../../../../../hooks/useResourceMeta";
import { useAfterCopy } from "../../../hooks/useAfterCopy";
import { JobSource, useCopyJobNotifications } from "../../../hooks/useCopyJobNotifications";
import { useCopyResources } from "../../../hooks/useCopyResources";
import { useUpdateMetaEffect } from "../../../hooks/useUpdateMetaEffect";

import { TableInfo } from "../../../../../../interfaces/resourceInfo";
import { CopyResourceMeta } from "../../../types";

type CopyTableMeta = CopyResourceMeta & {
  table: TableInfo;
};

export const useCopyTableJob = (): CopyTableMeta => {
  const currentResourceName: string = useSelector(dialogResourceSelector);
  const { table: currentTable, loading } = useTable(currentResourceName);
  const [previewUrl] = useTablePreview(currentTable && currentTable.name);
  const [meta, updateMeta] = useResourceMeta(getMetaFromResource(currentTable as ResourceInfoDc, previewUrl));
  const { copyJob, copyData: jobResult, copyLoading: jobLoading, copyError: jobError } = useCopyResources();
  const [waiting, setWaiting] = useState(false);

  const notificationInfos = useCopyJobNotifications(JobSource.Tables, { alias: meta.alias });
  const { setInnerTasks } = useAfterCopy(notificationInfos, { jobResult, jobLoading, jobError });

  useUpdateMetaEffect(currentTable as ResourceInfoDc, previewUrl, updateMeta);

  const onApply = useCallback(async () => {
    if (!currentTable || !meta) {
      return Promise.resolve("");
    }

    const { alias, description } = meta;
    const newResourceName: string = getResourceName();

    setWaiting(true);
    setInnerTasks([
      createFilterCopyTask({
        dataStorage: TaskDataType.Table,
        sourceName: currentResourceName,
        targetName: newResourceName,
      }),
    ]);

    await copyJob({
      input: [
        {
          type: ResourceType.Table,
          name: currentResourceName,
          copyName: newResourceName,
          copyAlias: alias,
          copyDescription: description,
          copyWith: null,
        },
      ],
    });

    return Promise.resolve(newResourceName);
  }, [copyJob, currentResourceName, currentTable, meta, setInnerTasks]);

  return {
    table: currentTable || {},
    meta,
    updateMeta,
    loading,
    onApply,
    waiting,
  };
};
